import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import Tooltip from 'scoreboard/components/tooltip'
import unwrapModel from 'scoreboard/components/unwrap-model'
import ChartDesignerPanel from './chart-designer-panel'
import activeContentTemplate from './y-axis-panel-active-content.hbs'
import AddSerieMenu from './serie/add-serie-menu'
import AddAnalyticsMenu from './analytics/add-analytics-menu'
import SerieBarSortSettingsView from './sort/serie-bar-sort-settings-view'
import featureMetadata from './feature-metadata'
import i18n from 'scoreboard/components/i18n'
import Dropzone from '../edit/dropzone'
import nonAxesPlotType from 'scoreboard/charts/non-axes-plot-type'
import { chart as isPolarChart } from 'scoreboard/charts/is-polar'
import mouseTracker from 'scoreboard/components/mouse-tracker'

/**
 * @extends ChartDesignerPanel
 */
const YAxisPanel = ChartDesignerPanel.extend(/** @lends YAxisPanel.prototype */{
	className: ChartDesignerPanel.prototype.className + ' y-axis-panel',

	events: {
		'click .add-serie': 'onAddSerieClick',
		'click .add-analytics': 'onAddAnalyticsClick',
		'dropShadow' : 'onDropShadow',
		'transitionend': 'onTransitionEnd'
	},

	/**
	 * @type ChartDesigner
	 */
	model: null,

	/** @type {ChartData} */
	data: null,

	/**
	 * @abstract
	 * @type String
	 */
	axis: null,

	initialize: function (options = {}) {
		ChartDesignerPanel.prototype.initialize.apply(this, arguments)
		this.data = options.data

		this.activeFeatureViews = []
		this.dropzones = []

		//re-render the panel anytime the features collection order was updated
		this.model.features.on('sort', (collection, opts) => {
			//remove the previous activeFeatureViews and rebuild
			_.invokeMap(this.activeFeatureViews, 'remove')
			this.activeFeatureViews = []
			this.render()
		})

		this.listenTo(this.model, 'change', () => {
			this.$('.title').html(i18n.get(this._getTitle()))
		})
	},

	render: function() {
		ChartDesignerPanel.prototype.render.apply(this, arguments)

		//drop hint starts off hidden
		this.toggleDropHint(false)

		if (nonAxesPlotType(this.model) || isPolarChart(this.model)) {
			this.$('.add-analytics').addClass('is-not-displayed')
			this.$('.add-serie').addClass('full-width')
		}

		// #37548 these features aren't supported by maps
		const plotType = this.model.get('plotType')
		if (plotType === 'tree-map' || plotType === 'filled-map' ||
			plotType === 'spike-map' || plotType === 'bubble-map') {
			this.$('.add-buttons, .click-add-line-container, .info').remove()
			this.$el.addClass('has-no-add-buttons')
		}

		return this
	},

	/**
	 * @override
	 */
	renderActiveContent: function (container) {
		let features = this.model.featuresByAxis(this.axis)

		if (container.children().length === 0) {
			container.html(activeContentTemplate(this))
		}

		features.forEach((feature, i) => {
			const featurePredicate = view => view.model === feature
			let view = _.find(this.activeFeatureViews, featurePredicate)
			if (!view) {
				const {type} = unwrapModel(feature)
				const {view: ConcreteFeatureView} = featureMetadata[type]
				view = new ConcreteFeatureView({
					model: feature,
					chart: this.model,
					panel: this
				})

				let previousView = this.activeFeatureViews[i-1]
				if (previousView) {
					previousView.$el.after(view.el)
				} else {
					this.$('.features').append(view.el)
				}

				this.listenTo(feature, 'remove', () => {
					view.remove()
					_.remove(this.activeFeatureViews, featurePredicate)
					this.renderActiveContent(this.activeContent())
				})

				this.listenTo(view, 'loading', () => this.trigger('loading'))

				// when one feature is edited, trigger the removal of all tooltips
				this.listenTo(view, 'edit', () => this.inactive())

				// #44190 don't emphasize serie plots while the panel is still
				// expanding
				// #46210 also don't emphasize if the panel is inactive (might
				// not have started expanding yet)
				view.$el.mouseenter(() => {
					this.active && !this.transitioning && this.emphasize(feature)
				})
				view.$el.mouseleave(() => !this.transitioning && this.emphasize())
				this.activeFeatureViews.push(view)

				view.render()
			}
		})

		this.renderBarSortSettings()
		this.renderDropzones()

		this.$('.chart-designer-panel-content')
			.toggleClass('is-empty', !this.activeFeatureViews.length)
	},

	onTransitionEnd: function (e) {
		if (e.target !== this.el) {
			// Only want to handle the transitionend event on the root element
			// to prevent unnecessary re-renders
			return
		}

		// #46438 once the panel is done transitioning open, if there is a feature
		// under the mouse, we want to emphasize it
		let {x, y} = mouseTracker.coords()
		let elementUnderMouse = document.elementFromPoint(x, y)

		let viewUnderMouse = this.activeFeatureViews.find(view => {
			return view.el == elementUnderMouse || $.contains(view.el, elementUnderMouse)
		})

		if (viewUnderMouse) {
			this.emphasize(viewUnderMouse.model)
		}
	},

	emphasize: function(feature = null) {
		const id = _.invoke(feature, 'get', 'id')
		this.trigger('emphasize', id)
	},

	renderBarSortSettings: function () {
		if (!this.barSortSettingsView) {
			this.barSortSettingsView = new SerieBarSortSettingsView({
				model: this.model.barSortSettings({ axis: this.axis }),
				chart: this.model,
				el: this.$('.sort-settings'),
				axis: this.axis
			})

			this.listenTo(this.barSortSettingsView, 'change', () => this.trigger('change:sortSettings'))
		}

		this.barSortSettingsView.render()
	},

	renderDropzones: function() {
		// remove the current dropzones
		this.$('.features > .dropzone').remove()
		_.invokeMap(this.dropzones, 'remove')
		this.dropzones = []

		// place an initial dropzone at top of the panel
		let model = _.isEmpty(this.activeFeatureViews) ?
			null : this.activeFeatureViews[0].model
		let topDropzone = new Dropzone({
			model,
			axis: this.axis,
			panel: this
		})
		this.dropzones.push(topDropzone)
		this.$('.features').prepend(topDropzone.el)
		topDropzone.render()

		_.each(this.activeFeatureViews, view => {
			let featureModel = view.model
			let bottomDropzone = new Dropzone({
				model: featureModel,
				panel: this
			})
			this.dropzones.push(bottomDropzone)

			// set the dropzones on the view
			view.topDropzone = topDropzone
			view.bottomDropzone = bottomDropzone

			// add a dropzone after each view
			view.$el.after(bottomDropzone.el)
			bottomDropzone.render()

			topDropzone = bottomDropzone
		})
	},

	onAddSerieClick: function () {
		this.addSerieTooltip && this.addSerieTooltip.remove()

		let addMenu = new AddSerieMenu({
			model: this.model,
			axis: this.axis
		})

		this.addSerieTooltip = new Tooltip({
			attachTo: this.$('.add-serie'),
			content: addMenu,
			placement: 'below',
			arrow: 'left',
			extraClass: 'has-bootslide-menu chart-designer-tooltip'
		}).render()
	},

	onAddAnalyticsClick: function () {
		this.addAnalyticsTooltip && this.addAnalyticsTooltip.remove()
		let addButton = this.$('.add-analytics')

		let addMenu = new AddAnalyticsMenu({
			model: this.model,
			axis: this.axis,
			addButton
		})

		this.addAnalyticsTooltip = new Tooltip({
			attachTo: addButton,
			content: addMenu,
			placement: 'below',
			arrow: 'left',
			extraClass: 'has-bootslide-menu add-analytics-menu chart-designer-tooltip'
		}).render()
	},

	toggleActive: function (active) {
		ChartDesignerPanel.prototype.toggleActive.apply(this, arguments)

		if (!active) {
			this.removeAddSerieTooltip()
			_.invokeMap(this.activeFeatureViews, 'inactive')
		}
	},

	toggleDropHint: function(show) {
		// hide the empty message if we are showing the drop hint
		if (_.isEmpty(this.activeFeatureViews)) {
			this.$('.empty-message').toggleClass('is-not-displayed', show)
		}

		this.$('.features')
		  	.toggleClass('drop-hint', show)
			.find('.dropzone')
			.removeClass('is-selected drop')
			.last()
			.toggleClass('is-selected', show)

		_.invokeMap(this.getAllDropzones(), 'showInactiveText')
		let lastDropzone = this.dropzones[this.dropzones.length - 1]
		show && lastDropzone.showInactiveText(
			'common.dragAndDropHint')
	},

	/**
	 * returns all dropzones in the panel, whether they are nested in a feature
	 * view or not
	 */
	getAllDropzones: function() {
		let featureDropzones = _.map(this.activeFeatureViews, afv =>
			_.isNil(afv.dropzones) ? [] : afv.dropzones)
		return _(this.dropzones)
			.concat(featureDropzones)
			.flatten()
			.value()
	},

	removeAddSerieTooltip: function () {
		this.addSerieTooltip && this.addSerieTooltip.remove()
		delete this.addSerieTooltip
	},

	removeAddAnalyticsTooltip: function() {
		this.addAnalyticsTooltip && this.addAnalyticsTooltip.remove()
		delete this.addAnalyticsTooltip
	},

	porterDestinations: function() {
		return this.$('.porter-destination')
	},

	inactive: function() {
		_.invokeMap(this.activeFeatureViews, 'inactive')
		this.removeAddSerieTooltip()
		this.removeAddAnalyticsTooltip()
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.removeAddSerieTooltip()
		_.invokeMap([...this.activeFeatureViews, ...this.dropzones,
			this.addAnalyticsTooltip, this.barSortSettingsView], 'remove')
	}
})

export default YAxisPanel

YAxisPanel.__filename__ = 'charts/edit/y-axis-panel.js'
